import {useI18n} from 'i18n/I18nContext'
import React, {useState} from 'react'
import {useInvoicesModals} from 'attendee/Invoices/InvoicesModalsProvider'
import {useAuth} from 'attendee/Auth/Providers/AuthProvider'
import {useAttendeeTransactions} from 'attendee/Invoices/Transactions/AttendeeTransactionsProvider'
import styled from 'styled-components'
import {useAttendeeInvoices} from 'attendee/Invoices/Invoices/AttendeeInvoicesProvider'

export default function CreateInvoiceModal() {
  const {t} = useI18n()
  const {
    createInvoiceModalOpen,
    setCreateInvoiceModalOpen,
    createInvoiceSelectedTransaction,
  } = useInvoicesModals()

  const {user, hasCompany} = useAuth()
  const {createInvoice} = useAttendeeTransactions()
  const {refresh} = useAttendeeInvoices()

  const onClose = () => {
    setCreateInvoiceModalOpen(false)
  }

  const [saving, setSaving] = useState(false)

  const handleCreateInvoice = () => {
    setSaving(true)

    if (!createInvoiceSelectedTransaction) return

    createInvoice(createInvoiceSelectedTransaction)
      .then(refresh)
      .finally(() => {
        setSaving(false)
        onClose()
      })
  }

  const [confirmed, setConfirmed] = useState(false)

  const canIssue =
    !!createInvoiceSelectedTransaction && !saving && confirmed && hasCompany()

  if (!createInvoiceModalOpen) return null

  return (
    <>
      <div
        onClick={onClose}
        className={`offcanvas__overlay ${createInvoiceModalOpen ? 'overlay-open' : ''}`}
      ></div>
      <section
        className={`speaker__popup-area larger ${createInvoiceModalOpen ? 'open' : ''}`}
      >
        <div className="popup__wrapper larger">
          <div className="popup__title-wrapper">
            <h3 className="popup__title">{t('attendee.invoiceModal.title')}</h3>
          </div>

          <div className="popup__input-wrapper">
            <CompanyCard>
              <CompanyHeader>
                <div>
                  <CompanyTitle>
                    {t('attendee.invoiceModal.sectionCompanyTitle')}
                  </CompanyTitle>
                  <CompanySubTitle>
                    {t('attendee.invoiceModal.reviewHint')}
                  </CompanySubTitle>
                </div>

                <MetaPill>
                  {t('attendee.invoiceModal.metaTransaction')}{' '}
                  <strong>{createInvoiceSelectedTransaction?.id ?? '—'}</strong>
                </MetaPill>
              </CompanyHeader>

              <CompanyGrid>
                <InfoRow>
                  <InfoLabel>
                    {t('attendee.invoiceModal.labelCompany')}
                  </InfoLabel>
                  <InfoValue>{user?.company_name}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t('attendee.invoiceModal.labelEik')}</InfoLabel>
                  <InfoValue>{user?.eik}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>
                    {t('attendee.invoiceModal.labelVatNumber')}
                  </InfoLabel>
                  <InfoValue>{user?.vat_number}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t('attendee.invoiceModal.labelMol')}</InfoLabel>
                  <InfoValue>{user?.mol}</InfoValue>
                </InfoRow>

                <Divider />

                <InfoRow>
                  <InfoLabel>
                    {t('attendee.invoiceModal.labelCountry')}
                  </InfoLabel>
                  <InfoValue>{user?.country}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>{t('attendee.invoiceModal.labelCity')}</InfoLabel>
                  <InfoValue>{user?.city}</InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>
                    {t('attendee.invoiceModal.labelPostalCode')}
                  </InfoLabel>
                  <InfoValue>{user?.postal_code}</InfoValue>
                </InfoRow>

                <InfoRow style={{gridColumn: '1 / -1'}}>
                  <InfoLabel>
                    {t('attendee.invoiceModal.labelAddress')}
                  </InfoLabel>
                  <InfoValue>{user?.address_line}</InfoValue>
                </InfoRow>
              </CompanyGrid>

              {!hasCompany() && (
                <WarningBox>
                  <WarningTitle>
                    {t('attendee.invoiceModal.missingCompanyTitle')}
                  </WarningTitle>
                  <WarningNote>
                    {t('attendee.invoiceModal.missingCompanyNote')}
                  </WarningNote>
                </WarningBox>
              )}

              <ConfirmRow>
                <input
                  id="confirm-company"
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  disabled={saving || !hasCompany()}
                />
                <label htmlFor="confirm-company">
                  {t('attendee.invoiceModal.confirmCheckbox')}
                </label>
              </ConfirmRow>

              <div className="input__btn-wrapper">
                <button
                  onClick={handleCreateInvoice}
                  disabled={!canIssue}
                  className={
                    canIssue
                      ? 'input__btn w-100 mb-20'
                      : 'disableBtnStyle w-100 mb-20'
                  }
                >
                  {saving
                    ? t('attendee.invoiceModal.submitSaving')
                    : t('attendee.invoiceModal.submit')}
                </button>
              </div>

              <FootNote>{t('attendee.invoiceModal.disclaimer')}</FootNote>
            </CompanyCard>
          </div>
        </div>
      </section>
    </>
  )
}

const CompanyCard = styled.div`
  display: grid;
  gap: 14px;
`

const CompanyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`

const CompanyTitle = styled.h4`
  margin: 0;
  font-size: 18px;
  line-height: 1.2;
`

const CompanySubTitle = styled.p`
  margin: 6px 0 0 0;
  font-size: 13px;
  opacity: 0.8;
`

const MetaPill = styled.div`
  white-space: nowrap;
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
`

const CompanyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`

const InfoRow = styled.div`
  display: grid;
  gap: 4px;
`

const InfoLabel = styled.div`
  font-size: 12px;
  opacity: 0.75;
`

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  word-break: break-word;
`

const Divider = styled.div`
  grid-column: 1 / -1;
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 4px 0;
`

const WarningBox = styled.div`
  border-radius: 4px;
  padding: 12px 14px;
  background: var(--clr-gradient-7);
  border: 1px solid rgba(255, 186, 0, 0.35);
`

const WarningTitle = styled.div`
  font-weight: 700;
  margin-bottom: 6px;
  color: #ffffff;
`

const WarningNote = styled.div`
  margin-top: 8px;
  font-size: 13px;
  opacity: 0.9;
  color: #ffffff;
`

const ConfirmRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;

  input {
    margin-top: 3px;
  }

  label {
    font-size: 13px;
    line-height: 1.35;
    opacity: 0.9;
    cursor: pointer;
    user-select: none;
  }
`

const FootNote = styled.div`
  margin-top: -6px;
  font-size: 12px;
  opacity: 0.75;
`
